// src/components/portal/serviceconnection.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import "./portal-components.css";
import { useAuth } from "../../context/AuthContext";
import { API_BASE_URL } from "../../config/apiConfig";

import IconAdd from "./res/Icon-Add.svg";
import IconEdit from "./res/Icon-Edit.svg";
import IconTrash from "./res/Icon-Trash.svg";
import IconGroup from "./res/Icon-Group.svg";
import IconDevice from "./res/Icon-Device.svg";
import IconRefresh from "./res/Icon-Refresh.svg";

const STATUS_POLL_INTERVAL = 5000; // 5 seconds

const ServiceConnection = () => {
  const { user } = useAuth();
  
  // Devices state
  const [devices, setDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(true);
  const [showDeviceForm, setShowDeviceForm] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  
  // Groups state
  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [selectedGroupDevices, setSelectedGroupDevices] = useState([]);
  const [showGroupDevicesModal, setShowGroupDevicesModal] = useState(null);
  
  // Form state
  const [deviceForm, setDeviceForm] = useState({
    hostname: "", ip_address: "", device_type: "cisco_ios", device_model: "", description: "", port: 22
  });
  const [groupForm, setGroupForm] = useState({ group_name: "", description: "" });
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const pollRef = useRef(null);

  // Fetch devices
  const fetchDevices = useCallback(async (showLoading = true) => {
    if (!user?.username) return;
    try {
      if (showLoading) setLoadingDevices(true);
      const res = await fetch(`${API_BASE_URL}/api/services/devices?username=${encodeURIComponent(user.username)}`);
      const data = await res.json();
      if (data.status === "success") {
        setDevices(data.data?.devices || []);
      }
    } catch (err) {
      console.error("Failed to fetch devices:", err);
    } finally {
      if (showLoading) setLoadingDevices(false);
    }
  }, [user?.username]);

  // Fetch groups
  const fetchGroups = useCallback(async (showLoading = true) => {
    if (!user?.username) return;
    try {
      if (showLoading) setLoadingGroups(true);
      const res = await fetch(`${API_BASE_URL}/api/services/groups?username=${encodeURIComponent(user.username)}`);
      const data = await res.json();
      if (data.status === "success") {
        setGroups(data.data?.groups || []);
      }
    } catch (err) {
      console.error("Failed to fetch groups:", err);
    } finally {
      if (showLoading) setLoadingGroups(false);
    }
  }, [user?.username]);

  // Initial fetch
  useEffect(() => {
    fetchDevices();
    fetchGroups();
  }, [fetchDevices, fetchGroups]);

  // Status polling
  useEffect(() => {
    pollRef.current = setInterval(() => {
      fetchDevices(false);
    }, STATUS_POLL_INTERVAL);
    return () => clearInterval(pollRef.current);
  }, [fetchDevices]);

  // Device CRUD
  const handleSaveDevice = async (e) => {
    e.preventDefault();
    setFormError("");
    
    if (!deviceForm.hostname.trim()) {
      setFormError("Hostname is required");
      return;
    }
    if (!deviceForm.ip_address.trim()) {
      setFormError("IP Address is required");
      return;
    }
    
    setSaving(true);
    try {
      const endpoint = editingDevice 
        ? `${API_BASE_URL}/api/services/devices/${editingDevice.id}`
        : `${API_BASE_URL}/api/services/devices`;
      
      const res = await fetch(endpoint, {
        method: editingDevice ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: user.username, ...deviceForm })
      });
      
      const data = await res.json();
      if (data.status === "success") {
        setShowDeviceForm(false);
        setEditingDevice(null);
        setDeviceForm({ hostname: "", ip_address: "", device_type: "cisco_ios", device_model: "", description: "", port: 22 });
        fetchDevices();
      } else {
        setFormError(data.reason || "Failed to save device");
      }
    } catch (err) {
      setFormError("Network error");
    } finally {
      setSaving(false);
    }
  };

  const handleEditDevice = (device) => {
    setEditingDevice(device);
    setDeviceForm({
      hostname: device.hostname,
      ip_address: device.ip_address,
      device_type: device.device_type || "cisco_ios",
      device_model: device.device_model || "",
      description: device.description || "",
      port: device.port || 22
    });
    setShowDeviceForm(true);
    setFormError("");
  };

  const handleDeleteDevice = async (device) => {
    if (!window.confirm(`Delete device "${device.hostname}"?`)) return;
    
    try {
      const res = await fetch(`${API_BASE_URL}/api/services/devices/${device.id}?username=${encodeURIComponent(user.username)}`, {
        method: "DELETE"
      });
      const data = await res.json();
      if (data.status === "success") {
        fetchDevices();
      }
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  // Group CRUD
  const handleSaveGroup = async (e) => {
    e.preventDefault();
    setFormError("");
    
    if (!groupForm.group_name.trim()) {
      setFormError("Group name is required");
      return;
    }
    
    setSaving(true);
    try {
      const endpoint = editingGroup 
        ? `${API_BASE_URL}/api/services/groups/${editingGroup.id}`
        : `${API_BASE_URL}/api/services/groups`;
      
      const res = await fetch(endpoint, {
        method: editingGroup ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: user.username, ...groupForm })
      });
      
      const data = await res.json();
      if (data.status === "success") {
        setShowGroupForm(false);
        setEditingGroup(null);
        setGroupForm({ group_name: "", description: "" });
        fetchGroups();
      } else {
        setFormError(data.reason || "Failed to save group");
      }
    } catch (err) {
      setFormError("Network error");
    } finally {
      setSaving(false);
    }
  };

  const handleEditGroup = (group) => {
    setEditingGroup(group);
    setGroupForm({
      group_name: group.group_name,
      description: group.description || ""
    });
    setShowGroupForm(true);
    setFormError("");
  };

  const handleDeleteGroup = async (group) => {
    if (!window.confirm(`Delete group "${group.group_name}"?`)) return;
    
    try {
      const res = await fetch(`${API_BASE_URL}/api/services/groups/${group.id}?username=${encodeURIComponent(user.username)}`, {
        method: "DELETE"
      });
      const data = await res.json();
      if (data.status === "success") {
        fetchGroups();
      }
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  const handleManageGroupDevices = async (group) => {
    setShowGroupDevicesModal(group);
    try {
      const res = await fetch(`${API_BASE_URL}/api/services/groups/${group.id}/devices?username=${encodeURIComponent(user.username)}`);
      const data = await res.json();
      if (data.status === "success") {
        setSelectedGroupDevices(data.data?.devices?.map(d => d.id) || []);
      }
    } catch (err) {
      console.error("Failed to fetch group devices:", err);
    }
  };

  const handleToggleDeviceInGroup = async (deviceId) => {
    const group = showGroupDevicesModal;
    const isInGroup = selectedGroupDevices.includes(deviceId);
    
    try {
      const res = await fetch(`${API_BASE_URL}/api/services/groups/${group.id}/devices/${deviceId}`, {
        method: isInGroup ? "DELETE" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: user.username })
      });
      
      if (res.ok) {
        setSelectedGroupDevices(prev => 
          isInGroup ? prev.filter(id => id !== deviceId) : [...prev, deviceId]
        );
        fetchGroups();
      }
    } catch (err) {
      console.error("Toggle failed:", err);
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'online': return 'status-online';
      case 'offline': return 'status-offline';
      case 'error': return 'status-error';
      default: return 'status-unknown';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Never";
    return new Date(dateStr).toLocaleString();
  };

  return (
    <>
      <div className="right-top">
        <div className="right-top-title">Service Connections</div>
      </div>

      <div className="right-middle service-middle">
        {/* DEVICES SECTION */}
        <div className="service-section">
          <div className="cell service-cell">
            <div className="cell-header">
              <span>Network Devices</span>
              <div className="header-actions">
                <button className="icon-btn refresh-btn" onClick={() => fetchDevices()} title="Refresh">
                  <img src={IconRefresh} alt="Refresh" />
                </button>
                <button className="icon-btn add-btn" onClick={() => { setShowDeviceForm(true); setEditingDevice(null); setDeviceForm({ hostname: "", ip_address: "", device_type: "cisco_ios", device_model: "", description: "", port: 22 }); setFormError(""); }}>
                  <img src={IconAdd} alt="Add" />
                  <span>Add Device</span>
                </button>
              </div>
            </div>
            <div className="cell-body">
              {loadingDevices ? (
                <div className="loading">Loading devices...</div>
              ) : devices.length === 0 ? (
                <div className="empty-state">
                  <img src={IconDevice} alt="No devices" className="empty-icon" />
                  <p>No devices added yet</p>
                  <p className="empty-hint">Add your network devices to start backing up configurations</p>
                </div>
              ) : (
                <div className="table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Hostname</th>
                        <th>IP Address</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>Last Connected</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {devices.map(device => (
                        <tr key={device.id}>
                          <td className="hostname-cell">
                            <span className="hostname">{device.hostname}</span>
                            {device.description && <span className="description">{device.description}</span>}
                          </td>
                          <td><code>{device.ip_address}</code></td>
                          <td>{device.device_type}</td>
                          <td>
                            <span className={`status-badge ${getStatusClass(device.connection_status)}`}>
                              <span className="status-dot"></span>
                              {device.connection_status || 'Unknown'}
                            </span>
                          </td>
                          <td className="date-cell">{formatDate(device.last_connected_at)}</td>
                          <td className="actions-cell">
                            <button className="icon-btn-sm edit" onClick={() => handleEditDevice(device)} title="Edit">
                              <img src={IconEdit} alt="Edit" />
                            </button>
                            <button className="icon-btn-sm delete" onClick={() => handleDeleteDevice(device)} title="Delete">
                              <img src={IconTrash} alt="Delete" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* GROUPS SECTION */}
        <div className="service-section">
          <div className="cell service-cell">
            <div className="cell-header">
              <span>Device Groups</span>
              <div className="header-actions">
                <button className="icon-btn add-btn" onClick={() => { setShowGroupForm(true); setEditingGroup(null); setGroupForm({ group_name: "", description: "" }); setFormError(""); }}>
                  <img src={IconAdd} alt="Add" />
                  <span>Add Group</span>
                </button>
              </div>
            </div>
            <div className="cell-body">
              {loadingGroups ? (
                <div className="loading">Loading groups...</div>
              ) : groups.length === 0 ? (
                <div className="empty-state">
                  <img src={IconGroup} alt="No groups" className="empty-icon" />
                  <p>No device groups created</p>
                  <p className="empty-hint">Create groups to organize devices and run batch backups</p>
                </div>
              ) : (
                <div className="groups-grid">
                  {groups.map(group => (
                    <div key={group.id} className="group-card">
                      <div className="group-header">
                        <img src={IconGroup} alt="Group" className="group-icon" />
                        <div className="group-info">
                          <span className="group-name">{group.group_name}</span>
                          <span className="device-count">{group.device_count || 0} devices</span>
                        </div>
                      </div>
                      {group.description && <p className="group-description">{group.description}</p>}
                      <div className="group-actions">
                        <button className="btn-secondary" onClick={() => handleManageGroupDevices(group)}>
                          Manage Devices
                        </button>
                        <button className="icon-btn-sm edit" onClick={() => handleEditGroup(group)}>
                          <img src={IconEdit} alt="Edit" />
                        </button>
                        <button className="icon-btn-sm delete" onClick={() => handleDeleteGroup(group)}>
                          <img src={IconTrash} alt="Delete" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* DEVICE FORM MODAL */}
      {showDeviceForm && (
        <div className="modal-overlay" onClick={() => setShowDeviceForm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingDevice ? "Edit Device" : "Add Device"}</h3>
              <button className="close-btn" onClick={() => setShowDeviceForm(false)}>×</button>
            </div>
            <form onSubmit={handleSaveDevice}>
              <div className="form-row">
                <label>Hostname *</label>
                <input type="text" value={deviceForm.hostname} onChange={e => setDeviceForm({...deviceForm, hostname: e.target.value})} placeholder="e.g., core-router-01" />
              </div>
              <div className="form-row">
                <label>IP Address *</label>
                <input type="text" value={deviceForm.ip_address} onChange={e => setDeviceForm({...deviceForm, ip_address: e.target.value})} placeholder="e.g., 192.168.1.1" />
              </div>
              <div className="form-row-half">
                <div className="form-row">
                  <label>Device Type</label>
                  <select value={deviceForm.device_type} onChange={e => setDeviceForm({...deviceForm, device_type: e.target.value})}>
                    <option value="cisco_ios">Cisco IOS</option>
                    <option value="cisco_nxos">Cisco NX-OS</option>
                    <option value="cisco_asa">Cisco ASA</option>
                    <option value="juniper">Juniper</option>
                    <option value="arista">Arista</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="form-row">
                  <label>SSH Port</label>
                  <input type="number" value={deviceForm.port} onChange={e => setDeviceForm({...deviceForm, port: parseInt(e.target.value) || 22})} />
                </div>
              </div>
              <div className="form-row">
                <label>Model (Optional)</label>
                <input type="text" value={deviceForm.device_model} onChange={e => setDeviceForm({...deviceForm, device_model: e.target.value})} placeholder="e.g., Cisco 3850" />
              </div>
              <div className="form-row">
                <label>Description (Optional)</label>
                <textarea value={deviceForm.description} onChange={e => setDeviceForm({...deviceForm, description: e.target.value})} placeholder="Enter description..." rows="2" />
              </div>
              {formError && <div className="form-error">{formError}</div>}
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowDeviceForm(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={saving}>{saving ? "Saving..." : "Save Device"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* GROUP FORM MODAL */}
      {showGroupForm && (
        <div className="modal-overlay" onClick={() => setShowGroupForm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingGroup ? "Edit Group" : "Add Group"}</h3>
              <button className="close-btn" onClick={() => setShowGroupForm(false)}>×</button>
            </div>
            <form onSubmit={handleSaveGroup}>
              <div className="form-row">
                <label>Group Name *</label>
                <input type="text" value={groupForm.group_name} onChange={e => setGroupForm({...groupForm, group_name: e.target.value})} placeholder="e.g., Core Routers" />
              </div>
              <div className="form-row">
                <label>Description (Optional)</label>
                <textarea value={groupForm.description} onChange={e => setGroupForm({...groupForm, description: e.target.value})} placeholder="Enter description..." rows="3" />
              </div>
              {formError && <div className="form-error">{formError}</div>}
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowGroupForm(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={saving}>{saving ? "Saving..." : "Save Group"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* GROUP DEVICES MODAL */}
      {showGroupDevicesModal && (
        <div className="modal-overlay" onClick={() => setShowGroupDevicesModal(null)}>
          <div className="modal modal-wide" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Manage Devices in "{showGroupDevicesModal.group_name}"</h3>
              <button className="close-btn" onClick={() => setShowGroupDevicesModal(null)}>×</button>
            </div>
            <div className="modal-body">
              {devices.length === 0 ? (
                <div className="empty-state">
                  <p>No devices available. Add devices first.</p>
                </div>
              ) : (
                <div className="device-checklist">
                  {devices.map(device => (
                    <label key={device.id} className="device-check-item">
                      <input 
                        type="checkbox" 
                        checked={selectedGroupDevices.includes(device.id)}
                        onChange={() => handleToggleDeviceInGroup(device.id)}
                      />
                      <span className="device-check-info">
                        <span className="device-name">{device.hostname}</span>
                        <span className="device-ip">{device.ip_address}</span>
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="form-actions">
              <button className="btn-primary" onClick={() => setShowGroupDevicesModal(null)}>Done</button>
            </div>
          </div>
        </div>
      )}

      <div className="right-bottom-bar"></div>
    </>
  );
};

export default ServiceConnection;
